<template>
  <section v-if="stats" class="seo-section" id="how-long-players-play">
    <h2>How Long Do Steam Players Play Per Day?</h2>
    <p>
      Real numbers from {{ stats.players.toLocaleString('en-US') }} steamplayti.me players, {{ range }}. Each
      figure is one player's total across all games on a day they played.
    </p>
    <div class="seo-stats">
      <div class="seo-stat">
        <div class="label">Typical day</div>
        <div class="value">{{ formatMinutes(stats.medianMinutes) }}</div>
      </div>
      <div class="seo-stat">
        <div class="label">Average day</div>
        <div class="value">{{ formatMinutes(stats.meanMinutes) }}</div>
      </div>
      <div class="seo-stat">
        <div class="label">Top 10% play</div>
        <div class="value">{{ formatMinutes(stats.p90Minutes) }}+</div>
      </div>
      <div class="seo-stat">
        <div class="label">Days over 4 hours</div>
        <div class="value">{{ overFourHours }}%</div>
      </div>
    </div>

    <div class="insight-grid">
      <div class="seo-card">
        <h3>How long a playing day lasts</h3>
        <ul class="insight-bars">
          <li v-for="b in buckets" :key="b.key" class="insight-bar-row">
            <span class="insight-bar-label">{{ b.label }}</span>
            <span class="insight-bar-track"><span class="insight-bar-fill" :style="{ width: barWidth(b.share, maxBucket) }" /></span>
            <span class="insight-bar-value">{{ b.share }}%</span>
          </li>
        </ul>
      </div>
      <div class="seo-card">
        <h3>Average playing day by weekday</h3>
        <ul class="insight-bars">
          <li v-for="w in weekdays" :key="w.day" class="insight-bar-row">
            <span class="insight-bar-label">{{ w.label }}</span>
            <span class="insight-bar-track"><span class="insight-bar-fill" :style="{ width: barWidth(w.meanMinutes, maxWeekday) }" /></span>
            <span class="insight-bar-value">{{ formatMinutes(w.meanMinutes) }}</span>
          </li>
        </ul>
      </div>
    </div>
    <p class="insight-note">
      Updated daily. Only people who use steamplayti.me are counted, so this describes players who track their time,
      not every Steam account.
    </p>
  </section>
</template>

<script>
import { loadSiteInsights, formatMinutes, formatRange } from '../utils/siteInsights';

const BUCKET_LABELS = {
  under1h: 'Under 1h',
  '1to2h': '1–2h',
  '2to4h': '2–4h',
  '4to8h': '4–8h',
  over8h: '8h+',
};

const DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

export default {
  name: 'SeoDailyPlaytimeInsights',
  data() {
    return { daily: null };
  },
  computed: {
    stats() {
      return this.daily?.stats || null;
    },
    range() {
      return formatRange(this.daily.from, this.daily.to);
    },
    buckets() {
      return this.stats.buckets.map(b => ({ ...b, label: BUCKET_LABELS[b.key] || b.key }));
    },
    maxBucket() {
      return Math.max(...this.buckets.map(b => b.share), 1);
    },
    overFourHours() {
      const share = this.stats.buckets
        .filter(b => b.key === '4to8h' || b.key === 'over8h')
        .reduce((sum, b) => sum + b.share, 0);

      return Math.round(share);
    },
    weekdays() {
      return this.stats.weekdays
        .filter(w => w.meanMinutes !== null)
        .map(w => ({ ...w, label: DAY_LABELS[w.day] || w.day }));
    },
    maxWeekday() {
      return Math.max(...this.weekdays.map(w => w.meanMinutes), 1);
    },
  },
  async mounted() {
    const insights = await loadSiteInsights();

    this.daily = insights?.daily || null;
  },
  methods: {
    formatMinutes,
    barWidth(value, max) {
      return `${Math.max(2, Math.round((value / max) * 100))}%`;
    },
  },
};
</script>

<style scoped>
.insight-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 20px;
}

.insight-bars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.insight-bar-row {
  display: grid;
  grid-template-columns: 72px 1fr auto;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
}

.insight-bar-label {
  color: var(--muted);
  white-space: nowrap;
}

.insight-bar-track {
  height: 10px;
  border-radius: 999px;
  background: rgba(102, 192, 244, 0.12);
  overflow: hidden;
}

.insight-bar-fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--primary);
}

.insight-bar-value {
  color: var(--text);
  font-weight: 600;
  min-width: 52px;
  text-align: right;
}

.seo-section .insight-note {
  font-size: 0.85rem;
}
</style>
